import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DetailedAnalyticsTable = ({ data, loading }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const mockData = [
    {
      id: 1,
      store: 'Dhaka - Gulshan',
      brand: 'Coca-Cola',
      totalCost: 285000,
      installations: 8,
      avgCostPerInstall: 35625,
      completionRate: 95,
      roi: 8.5,
      lastUpdate: '2024-09-28',
      status: 'Active'
    },
    {
      id: 2,
      store: 'Dhaka - Dhanmondi',
      brand: 'Samsung',
      totalCost: 420000,
      installations: 6,
      avgCostPerInstall: 70000,
      completionRate: 100,
      roi: 12.3,
      lastUpdate: '2024-09-27',
      status: 'Active'
    },
    {
      id: 3,
      store: 'Chittagong - Agrabad',
      brand: 'Pepsi',
      totalCost: 195000,
      installations: 5,
      avgCostPerInstall: 39000,
      completionRate: 80,
      roi: 6.8,
      lastUpdate: '2024-09-26',
      status: 'Active'
    },
    {
      id: 4,
      store: 'Sylhet - Zindabazar',
      brand: 'LG',
      totalCost: 315000,
      installations: 7,
      avgCostPerInstall: 45000,
      completionRate: 85.7,
      roi: 9.2,
      lastUpdate: '2024-09-25',
      status: 'Active'
    },
    {
      id: 5,
      store: 'Rajshahi - Shaheb Bazar',
      brand: 'Nestlé',
      totalCost: 165000,
      installations: 9,
      avgCostPerInstall: 18333,
      completionRate: 88.9,
      roi: 5.4,
      lastUpdate: '2024-09-24',
      status: 'Active'
    },
    {
      id: 6,
      store: 'Dhaka - Gulshan',
      brand: 'Samsung',
      totalCost: 380000,
      installations: 4,
      avgCostPerInstall: 95000,
      completionRate: 100,
      roi: 15.2,
      lastUpdate: '2024-09-23',
      status: 'Active'
    },
    {
      id: 7,
      store: 'Dhaka - Dhanmondi',
      brand: 'Coca-Cola',
      totalCost: 225000,
      installations: 6,
      avgCostPerInstall: 37500,
      completionRate: 83.3,
      roi: 7.1,
      lastUpdate: '2024-09-22',
      status: 'Pending'
    },
    {
      id: 8,
      store: 'Chittagong - Agrabad',
      brand: 'LG',
      totalCost: 290000,
      installations: 5,
      avgCostPerInstall: 58000,
      completionRate: 100,
      roi: 11.8,
      lastUpdate: '2024-09-21',
      status: 'Active'
    }
  ];

  const tableData = data || mockData;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    let sortableData = [...tableData];
    
    if (searchTerm) {
      sortableData = sortableData?.filter(item =>
        item?.store?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.brand?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.status?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (sortConfig?.key) {
      sortableData?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableData;
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);
  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatCurrency = (value) => {
    return `৳${value?.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'Pending': { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      'Inactive': { bg: 'bg-error/10', text: 'text-error', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.['Pending'];

    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        {status}
      </div>
    );
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} />
            </div>
            <span>Loading analytics data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Detailed Cost Analysis</h3>
          <p className="text-sm text-muted-foreground">Store-wise branding performance metrics</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Input
            type="search"
            placeholder="Search stores, brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-64"
          />
          <Button variant="outline" iconName="Filter">
            Filter
          </Button>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('store')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Store Location
                  {getSortIcon('store')}
                </Button>
              </th>
              <th className="text-left py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('brand')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Brand
                  {getSortIcon('brand')}
                </Button>
              </th>
              <th className="text-right py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('totalCost')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Total Cost
                  {getSortIcon('totalCost')}
                </Button>
              </th>
              <th className="text-center py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('installations')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Installations
                  {getSortIcon('installations')}
                </Button>
              </th>
              <th className="text-right py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('avgCostPerInstall')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Avg Cost/Install
                  {getSortIcon('avgCostPerInstall')}
                </Button>
              </th>
              <th className="text-center py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('completionRate')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  Completion Rate
                  {getSortIcon('completionRate')}
                </Button>
              </th>
              <th className="text-center py-3 px-4">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('roi')}
                  className="gap-2 p-0 h-auto font-semibold text-foreground hover:text-primary"
                >
                  ROI
                  {getSortIcon('roi')}
                </Button>
              </th>
              <th className="text-center py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData?.map((item) => (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{item?.store}</div>
                  <div className="text-xs text-muted-foreground">Updated {item?.lastUpdate}</div>
                </td>
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{item?.brand}</div>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="font-semibold text-foreground">{formatCurrency(item?.totalCost)}</div>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="font-medium text-foreground">{item?.installations}</div>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="font-medium text-foreground">{formatCurrency(item?.avgCostPerInstall)}</div>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="font-medium text-foreground">{item?.completionRate}%</div>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="font-medium text-success">{item?.roi}%</div>
                </td>
                <td className="py-3 px-4 text-center">
                  {getStatusBadge(item?.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginatedData?.map((item) => (
          <div key={item?.id} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-foreground">{item?.store}</div>
                <div className="text-sm text-muted-foreground">{item?.brand}</div>
              </div>
              {getStatusBadge(item?.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Total Cost</div>
                <div className="font-semibold text-foreground">{formatCurrency(item?.totalCost)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Installations</div>
                <div className="font-medium text-foreground">{item?.installations}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Completion Rate</div>
                <div className="font-medium text-foreground">{item?.completionRate}%</div>
              </div>
              <div>
                <div className="text-muted-foreground">ROI</div>
                <div className="font-medium text-success">{item?.roi}%</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} results
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8"
                >
                  {page}
                </Button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedAnalyticsTable;